import { useAdminTariffsQuery } from '@streamster/shared/api/api.admin';
import { CouponCodeRules } from '@streamster/shared/api/types';
import mixins from '@streamster/shared/app/styles';
import Button from '@streamster/shared/components/Button/Button';
import {
  InputWithLabel,
  inputWithLabelFieldStyles,
} from '#/components/InputWIthLabel/InputWIthLabel';
import Select from '#/components/Select/Select';
import React, { useState } from 'react';
import { View } from 'react-native-web';

const initialRules: CouponCodeRules = {
  discountType: 'percentage',
  discountValue: 10,
  transactionType: 'first',
};

const couponTypes = [
  { label: 'Tariff', value: 'tariff' as const },
  { label: 'Discount', value: 'discount' as const },
];

const discountTypes = [
  { label: 'Percentage', value: 'percentage' as const },
  { label: 'Amount', value: 'amount' as const },
];

interface Props {
  initial?: {
    code?: string | null;
    type?: 'tariff' | 'discount';
    tariffId?: number | null;
    rules?: CouponCodeRules | null;
    usagesLeft?: number;
  };
  onSubmit: (payload: {
    code: string | null;
    rules: any;
    tariffId: number | null;
    type: 'tariff' | 'discount';
    usagesLeft: number;
  }) => Promise<any>;
  submitLabel?: string;
  disableCodeInput?: boolean;
  onCancel?: () => void;
  onDelete?: (() => Promise<any>) | null;
}

const CouponForm: React.FC<Props> = ({
  initial,
  onSubmit,
  submitLabel = 'Save',
  disableCodeInput,
  onCancel,
  onDelete,
}) => {
  const { data: tariffs } = useAdminTariffsQuery();

  const [rules, setRules] = useState<CouponCodeRules>(initial?.rules ?? initialRules);
  const [code, setCode] = useState(initial?.code ?? '');
  const [tariffId, setTariffId] = useState<number | null>(initial?.tariffId ?? null);
  const [usagesLeft, setUsagesLeft] = useState<number>(initial?.usagesLeft ?? 1);
  const [type, setType] = useState<'tariff' | 'discount'>(initial?.type ?? 'discount');

  const handleSubmit = async () => {
    await onSubmit({
      code: code && code.trim().length > 0 ? code.trim() : null,
      rules: type === 'discount' ? rules : {},
      tariffId: type === 'tariff' && tariffId ? tariffId : null,
      type,
      usagesLeft,
    });
  };

  return (
    <View style={[mixins().styles.flexColumn, mixins().styles.gap15, { maxWidth: 500 }]}>
      {disableCodeInput ? (
        <InputWithLabel name="Code" placeholder="Leave empty to auto generate">
          <div style={{ padding: 10 }}>{code}</div>
        </InputWithLabel>
      ) : (
        <InputWithLabel
          name="Code"
          placeholder="Leave empty to auto generate"
          value={code ?? ''}
          onChange={text => setCode(text)}
        />
      )}

      <InputWithLabel name="Type">
        <Select
          style={inputWithLabelFieldStyles.select}
          values={couponTypes}
          handleChange={v => setType(v)}
          value={type}
        />
      </InputWithLabel>

      {type === 'tariff' ? (
        <InputWithLabel name="Tariff">
          <Select
            style={inputWithLabelFieldStyles.select}
            values={
              tariffs ? tariffs.map(tariff => ({ label: tariff.name, value: tariff.id })) : []
            }
            handleChange={v => setTariffId(Number(v))}
            value={tariffId || ''}
          />
        </InputWithLabel>
      ) : (
        <>
          <InputWithLabel name="Discount Type">
            <Select
              style={inputWithLabelFieldStyles.select}
              values={discountTypes}
              handleChange={v => setRules({ ...rules, discountType: v })}
              value={rules.discountType}
            />
          </InputWithLabel>
          <InputWithLabel
            name="Discount Value"
            value={String(rules.discountValue)}
            onChange={text => setRules({ ...rules, discountValue: Number(text) })}
          />
          <InputWithLabel
            name="Number of usages"
            value={String(usagesLeft)}
            onChange={text => setUsagesLeft(Number(text))}
          />
        </>
      )}

      <View style={[mixins().styles.flexRow, mixins().styles.gap15]}>
        {onDelete && (
          <Button onPress={onDelete} secondary style={{ marginRight: 'auto' }}>
            Remove
          </Button>
        )}
        {onCancel && (
          <Button onPress={onCancel} secondary>
            Cancel
          </Button>
        )}
        <Button style={{ marginLeft: 'auto' }} onPress={handleSubmit}>
          {submitLabel}
        </Button>
      </View>
    </View>
  );
};

export default CouponForm;
